import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiSun, FiMoon, FiGrid, FiChevronLeft, FiChevronRight, FiPlus, FiPrinter,
  FiTrash2, FiCalendar, FiEdit2, FiCheck, FiX, FiPercent, FiSearch,
} from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';
import { useAuth } from '../context/AuthContext';
import { useModule } from '../context/ModuleContext';
import ReceiptPreview from '../components/ReceiptPreview';
import { getReceiptType } from '../config/receipts.config';
import * as gpService from '../services/goldPurchaseService';
import '../receipt-print.css';
import '../gold-purchase.css';

/* =========================================================================
 * Helpers (pure)
 * ========================================================================= */

function money(n) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function grams3(n) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function toISODate(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addDays(iso, delta) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return toISODate(dt);
}

function prettyDate(iso, locale) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(locale === 'pt-BR' ? 'pt-BR' : 'en-US', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });
}

// Sum the amount SPENT (paid out) in a set of rows, per currency.
// This is the daily spend total — it does NOT carry between days.
function sumSpent(rows) {
  return rows.reduce((acc, r) => ({
    usd: acc.usd + (parseFloat(r.paid_usd) || 0),
    srd: acc.srd + (parseFloat(r.paid_srd) || 0),
    eur: acc.eur + (parseFloat(r.paid_eur) || 0),
  }), { usd: 0, srd: 0, eur: 0 });
}

// Sum grams of gold bought in a set of rows.
function sumGold(rows) {
  return rows.reduce((acc, r) => acc + (parseFloat(r.grams) || 0), 0);
}

// Replay a day's gold events (purchases + adjustments) in chronological order
// onto the carried opening total. A purchase adds grams; 'set' resets the
// running total; 'subtract' removes grams. Ordering by created_at (then id) so a
// reset only zeroes what came before it — later purchases still count.
function replayGold(opening, rows, adjustments) {
  const events = [];
  for (const r of rows) {
    events.push({ ts: parseInt(r.created_at) || 0, id: parseInt(r.id) || 0, kind: 'buy', amount: parseFloat(r.grams) || 0 });
  }
  for (const a of adjustments) {
    events.push({ ts: parseInt(a.created_at) || 0, id: parseInt(a.id) || 0, kind: a.type, amount: parseFloat(a.amount) || 0 });
  }
  events.sort((x, y) => (x.ts - y.ts) || (x.id - y.id));

  let g = opening;
  for (const e of events) {
    if (e.kind === 'buy') g += e.amount;
    else if (e.kind === 'set') g = e.amount;
    else if (e.kind === 'subtract') g -= e.amount;
  }
  return g;
}

// A numeric field value for editing: show empty string for 0/blank so inputs
// aren't cluttered with 0.00.
function editVal(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) && n !== 0 ? String(v) : '';
}

/* =========================================================================
 * Component
 * ========================================================================= */

export default function GoldPurchaseDashboard() {
  const { user, logout } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { theme, toggleTheme } = useTheme();
  const { goToPicker } = useModule();

  const [date, setDate] = useState(() => toISODate(new Date()));
  const [rows, setRows] = useState([]);
  const [defaultRate, setDefaultRateState] = useState(37);
  const [goldOpening, setGoldOpening] = useState(0);
  const [goldAdjustments, setGoldAdjustments] = useState([]);
  const [showGoldModal, setShowGoldModal] = useState(false);
  const [showTeor, setShowTeor] = useState(false);
  const [searchNote, setSearchNote] = useState('');
  const [searchError, setSearchError] = useState('');
  const [highlightId, setHighlightId] = useState(null);

  // Draft row being added/edited: null | {id|null, grams, price_per_gram, ...}.
  // id === null => a new unsaved row; otherwise editing an existing row.
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  // Print state: which row's receipt to render + whether in print-only mode.
  const [printRow, setPrintRow] = useState(null);
  const [printing, setPrinting] = useState(false);

  // ---- Live sync (polling) state ----
  const lastVersion = useRef('');     // last day-version we've applied
  const pendingSync = useRef(false);  // a change arrived while a draft was open
  // Refs mirror state so the polling interval reads fresh values.
  const dateRef = useRef(date);       dateRef.current = date;
  const draftRef = useRef(draft);     draftRef.current = draft;
  const printingRef = useRef(printing); printingRef.current = printing;

  // Load a day's data from the API. Also captures the current version so the
  // poller only reloads on real changes.
  const loadDay = async (iso) => {
    const res = await gpService.getDay(iso);
    if (res.success && res.data) {
      setRows(res.data.rows || []);
      setGoldAdjustments(res.data.adjustments || []);
      setGoldOpening(parseFloat(res.data.gold_opening) || 0);
      setDefaultRateState(parseFloat(res.data.default_rate) || 37);
      const v = await gpService.getDayVersion(iso);
      if (v.success && v.data) lastVersion.current = v.data.version;
    }
  };

  useEffect(() => {
    loadDay(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Preload the receipt logo so print never stalls waiting for the image.
  useEffect(() => {
    const img = new Image();
    img.src = process.env.PUBLIC_URL + '/assets/logo_receipt.PNG';
  }, []);

  // ---- Live sync: poll the day version every 4s (only when tab visible).
  // On a real change: reload the day, unless a draft is open or printing — in
  // that case defer the reload until the draft closes (never lose typing).
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      if (printingRef.current) return;
      const iso = dateRef.current;
      const v = await gpService.getDayVersion(iso);
      if (cancelled || !v.success || !v.data) return;
      if (v.data.version === lastVersion.current) return;
      // Change detected.
      if (draftRef.current) {
        pendingSync.current = true; // apply after the user finishes editing
      } else {
        await loadDay(iso);
      }
    };
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a draft closes and a remote change was buffered, apply it now.
  useEffect(() => {
    if (!draft && pendingSync.current) {
      pendingSync.current = false;
      loadDay(dateRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Cash "spent today" — the day's payout totals. Resets each day (no carry).
  const spent = useMemo(() => sumSpent(rows), [rows]);

  // Gold: opening (carried) + today's buys and adjustments, replayed in
  // chronological order so a reset only zeroes what came before it (later buys
  // the same day still count).
  const goldBought = useMemo(() => sumGold(rows), [rows]);
  const goldClosing = useMemo(
    () => replayGold(goldOpening, rows, goldAdjustments),
    [goldOpening, rows, goldAdjustments]
  );

  const dayTotals = useMemo(() => {
    return rows.reduce((acc, r) => ({
      usd: acc.usd + (parseFloat(r.total_usd) || 0),
      srd: acc.srd + (parseFloat(r.total_srd) || 0),
    }), { usd: 0, srd: 0 });
  }, [rows]);

  /* ---- Gold adjustments (reset / subtract) ---- */
  const applyGoldAction = async (type, amount) => {
    setShowGoldModal(false);
    const res = await gpService.addAdjustment(date, type, parseFloat(amount) || 0);
    if (res.success) await loadDay(date);
    else alert(res.message || 'Failed to adjust gold');
  };
  const undoLastGoldAdjustment = async () => {
    const res = await gpService.undoAdjustment(date);
    if (res.success) await loadDay(date);
  };

  /* ---- Row editing (draft -> confirm -> read-only -> edit) ---- */

  // Begin adding a NEW row: opens an editable draft (not yet in the DB).
  const startAdd = () => {
    setDraft({
      id: null,
      grams: '',
      price_per_gram: '',
      rate_srd: String(defaultRate),
      paid_usd: '',
      paid_srd: '',
      paid_eur: '',
    });
  };

  // Begin editing an existing saved row.
  const startEdit = (row) => {
    setDraft({
      id: row.id,
      grams: row.grams,
      price_per_gram: row.price_per_gram,
      rate_srd: row.rate_srd,
      paid_usd: row.paid_usd,
      paid_srd: row.paid_srd,
      paid_eur: row.paid_eur,
    });
  };

  const setDraftField = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  const cancelDraft = () => setDraft(null);

  // Confirm the draft: create (if new) or update (if editing), then reload.
  const confirmDraft = async () => {
    if (!draft || saving) return;
    setSaving(true);
    try {
      let id = draft.id;
      if (!id) {
        const created = await gpService.addRow(date);
        if (!created.success || !created.data) {
          alert(created.message || 'Failed to add row');
          return;
        }
        id = created.data.id;
      }
      const res = await gpService.updateRow(id, {
        grams: draft.grams,
        price_per_gram: draft.price_per_gram,
        rate_srd: draft.rate_srd,
        paid_usd: draft.paid_usd,
        paid_srd: draft.paid_srd,
        paid_eur: draft.paid_eur,
      });
      if (res.success) {
        setDraft(null);
        await loadDay(date);
      } else {
        alert(res.message || 'Failed to save row');
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm(t('gp_delete_confirm'))) return;
    const res = await gpService.deleteRow(id);
    if (res.success) await loadDay(date);
    else alert(res.message || 'Failed to delete row');
  };

  /* ---- Rate ---- */
  const commitDefaultRate = async (val) => {
    const r = parseFloat(val);
    if (!Number.isFinite(r) || r <= 0) return;
    setDefaultRateState(r);
    await gpService.setDefaultRate(r);
  };

  /* ---- Search by note number ---- */
  const runSearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const q = searchNote.trim();
    if (!q) return;
    setSearchError('');
    const res = await gpService.findByNote(q);
    if (res.success && res.data) {
      const d = res.data.purchase_date;
      if (d && d !== date) setDate(d);   // jump to its day (triggers loadDay)
      setHighlightId(res.data.id);       // highlight the matched row
      setTimeout(() => setHighlightId(null), 4000);
    } else {
      setSearchError(res.message || 'Not found');
      setTimeout(() => setSearchError(''), 3000);
    }
  };

  /* ---- Date nav ---- */
  const goPrev = () => setDate((d) => addDays(d, -1));
  const goNext = () => setDate((d) => addDays(d, 1));
  const goToday = () => setDate(toISODate(new Date()));
  const isToday = date === toISODate(new Date());

  /* ---- Print ---- */
  const receiptType = useMemo(() => getReceiptType('compra_ouro'), []);
  const printValues = useMemo(() => {
    if (!printRow) return {};
    return {
      note_number: printRow.note_number,
      peso: printRow.grams,
      preco: printRow.price_per_gram,
      total: printRow.total_usd,
      pago_usd: printRow.paid_usd,
      pago_srd: printRow.paid_srd,
      pago_eur: printRow.paid_eur,
    };
  }, [printRow]);

  const handlePrint = (row) => {
    // Enter print mode: the component renders ONLY the receipt (the whole ledger
    // is unmounted), so the browser never lays the wide table out for print.
    setPrintRow(row);
    setPrinting(true);
    setTimeout(() => {
      const done = () => {
        setPrinting(false);
        window.removeEventListener('afterprint', done);
      };
      window.addEventListener('afterprint', done);
      window.print();
      setTimeout(done, 1500);
    }, 150);
  };

  // Print mode: render nothing but the receipt. receipt-print.css handles the
  // @media print sizing; on screen this is briefly shown then removed.
  if (printing) {
    return (
      <div className="gp-print-only">
        <ReceiptPreview type={receiptType} values={printValues} currency="USD" t={t} />
      </div>
    );
  }

  return (
    <div className="layout">
      <header className="app-header">
        <div className="brand">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="Logo" />
          <h1 className="app-title">{t('brand_title')}</h1>
        </div>
        <div className="app-actions">
          <button className="btn btn-ghost btn-icon" onClick={goToPicker} title={t('back_to_modules')} aria-label={t('back_to_modules')}>
            <FiGrid />
          </button>
          <button
            className="btn btn-ghost btn-icon"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          <select className="lang-select" value={locale} onChange={(e) => setLocale(e.target.value)} aria-label="Language" title="Language">
            <option value="en">English</option>
            <option value="pt-BR">Português (BR)</option>
          </select>
          <span className="muted">{t('signed_in_as')} {user?.username}</span>
          <button className="btn btn-ghost" onClick={logout}>{t('logout')}</button>
        </div>
      </header>

      <main className="content gp-content">
        {/* Date navigation bar */}
        <div className="gp-datebar">
          <button className="btn btn-ghost btn-icon" onClick={goPrev} title="Previous day"><FiChevronLeft /></button>
          <div className="gp-date-display">
            <FiCalendar />
            <span className="gp-date-text">{prettyDate(date, locale)}</span>
            <input
              type="date"
              className="gp-date-input"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
            />
          </div>
          <button className="btn btn-ghost btn-icon" onClick={goNext} title="Next day"><FiChevronRight /></button>
          {!isToday && (
            <button className="btn btn-ghost btn-sm" onClick={goToday}>{t('gp_today')}</button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => setShowTeor(true)} title={t('gp_teor_calc')}>
            <FiPercent style={{ marginRight: '6px' }} /> {t('gp_teor_calc')}
          </button>

          <form className="gp-search" onSubmit={runSearch}>
            <div className="input-group gp-search-input">
              <span className="prefix" aria-hidden><FiSearch /></span>
              <input
                type="text"
                value={searchNote}
                onChange={(e) => setSearchNote(e.target.value)}
                placeholder={t('gp_search_note')}
                aria-label={t('gp_search_note')}
              />
            </div>
            {searchError && <span className="gp-search-error">{searchError}</span>}
          </form>

          <div className="gp-rate-control">
            <span className="muted small">{t('gp_default_rate')}</span>
            <div className="input-group gp-rate-input">
              <span className="prefix" aria-hidden>1$=</span>
              <input
                type="number"
                step="0.001"
                inputMode="decimal"
                defaultValue={defaultRate}
                onBlur={(e) => commitDefaultRate(e.target.value)}
                title={t('gp_exchange_rate')}
              />
              <span className="prefix" aria-hidden>SRD</span>
            </div>
          </div>
        </div>

        {/* Summary bar */}
        <div className="gp-balance-bar">
          {/* Cash spent today (does not carry between days) */}
          <div className="gp-bal-group">
            <span className="gp-bal-label">{t('gp_spent_today')}</span>
            <BalInline bal={spent} tone="neg" />
          </div>

          <div className="gp-bal-divider" />

          {/* Gold — carries day to day; reset/subtract via the modal */}
          <div className="gp-bal-group">
            <span className="gp-bal-label">
              {t('gp_gold_opening')}
              {goldOpening > 0 && <span className="gp-carry" title="Carried from previous day"> ↳</span>}
            </span>
            <span className="gp-gold-val">{grams3(goldOpening)} g</span>
          </div>

          <span className="gp-bal-sep gp-bal-sep-plus">+</span>

          <div className="gp-bal-group">
            <span className="gp-bal-label">{t('gp_gold_bought')}</span>
            <span className="gp-gold-val gp-gold-pos">{grams3(goldBought)} g</span>
          </div>

          {goldAdjustments.length > 0 && (
            <div className="gp-bal-group">
              <span className="gp-bal-label">{t('gp_gold_adjustments')}</span>
              <span className="gp-gold-val gp-gold-neg">
                {grams3(goldClosing - goldOpening - goldBought)} g
              </span>
              <button className="btn btn-ghost btn-sm" onClick={undoLastGoldAdjustment} title={t('gp_undo')}>
                {t('gp_undo')}
              </button>
            </div>
          )}

          <span className="gp-bal-sep">=</span>

          <div className="gp-bal-group gp-bal-group-gold">
            <span className="gp-bal-label">{t('gp_gold_closing')}</span>
            <span className="gp-gold-val gp-gold-strong">{grams3(goldClosing)} g</span>
            <button className="btn btn-ghost btn-sm gp-gold-action" onClick={() => setShowGoldModal(true)}>
              {t('gp_adjust_gold')}
            </button>
          </div>
        </div>

        {/* Purchases table */}
        <div className="gp-table-wrap">
          <table className="gp-table">
            <thead>
              <tr>
                <th>{t('gp_note')}</th>
                <th className="gp-num">{t('gp_grams')}</th>
                <th className="gp-num">{t('gp_price')}</th>
                <th className="gp-num">{t('gp_total_usd')}</th>
                <th className="gp-num">{t('gp_total_srd')}</th>
                <th className="gp-num">{t('gp_rate')}</th>
                <th className="gp-num gp-paid-col">{t('gp_paid_usd')}</th>
                <th className="gp-num gp-paid-col">{t('gp_paid_srd')}</th>
                <th className="gp-num gp-paid-col">{t('gp_paid_eur')}</th>
                <th className="gp-actions-col"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !draft && (
                <tr>
                  <td colSpan={10} className="gp-empty muted">{t('gp_no_rows')}</td>
                </tr>
              )}
              {rows.map((r) => {
                // Row being edited -> editable draft row.
                if (draft && draft.id === r.id) {
                  return (
                    <DraftRow
                      key={r.id}
                      note={r.note_number}
                      draft={draft}
                      defaultRate={defaultRate}
                      saving={saving}
                      onField={setDraftField}
                      onConfirm={confirmDraft}
                      onCancel={cancelDraft}
                      t={t}
                    />
                  );
                }
                // Read-only saved row.
                return (
                  <tr key={r.id} className={highlightId === r.id ? 'gp-row-highlight' : undefined}>
                    <td className="gp-note">{r.note_number}</td>
                    <td className="gp-num gp-ro">{money(r.grams)}</td>
                    <td className="gp-num gp-ro">{money(r.price_per_gram)}</td>
                    <td className="gp-num gp-computed">$ {money(r.total_usd)}</td>
                    <td className="gp-num gp-computed">SRD {money(r.total_srd)}</td>
                    <td className="gp-num gp-ro">{money(r.rate_srd)}</td>
                    <td className="gp-num gp-paid-col gp-ro">{money(r.paid_usd)}</td>
                    <td className="gp-num gp-paid-col gp-ro">{money(r.paid_srd)}</td>
                    <td className="gp-num gp-paid-col gp-ro">{money(r.paid_eur)}</td>
                    <td className="gp-actions-col">
                      <div className="gp-row-actions">
                        <button className="btn btn-ghost btn-icon" title={t('gp_edit')} onClick={() => startEdit(r)} disabled={!!draft}>
                          <FiEdit2 />
                        </button>
                        <button className="btn btn-ghost btn-icon" title={t('gp_print')} onClick={() => handlePrint(r)} disabled={!!draft}>
                          <FiPrinter />
                        </button>
                        <button className="btn btn-ghost btn-icon" title={t('gp_delete')} onClick={() => deleteRow(r.id)} disabled={!!draft}>
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {/* New (unsaved) draft row */}
              {draft && draft.id === null && (
                <DraftRow
                  note={t('gp_new_row')}
                  draft={draft}
                  defaultRate={defaultRate}
                  saving={saving}
                  onField={setDraftField}
                  onConfirm={confirmDraft}
                  onCancel={cancelDraft}
                  t={t}
                />
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td className="gp-foot-label">{t('gp_day_total')}</td>
                  <td className="gp-num gp-foot-total">{grams3(goldBought)} g</td>
                  <td></td>
                  <td className="gp-num gp-foot-total">$ {money(dayTotals.usd)}</td>
                  <td className="gp-num gp-foot-total">SRD {money(dayTotals.srd)}</td>
                  <td></td>
                  <td className="gp-num gp-foot-total">$ {money(spent.usd)}</td>
                  <td className="gp-num gp-foot-total">SRD {money(spent.srd)}</td>
                  <td className="gp-num gp-foot-total">€ {money(spent.eur)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>

          <button className="btn btn-primary gp-add-btn" onClick={startAdd} disabled={!!draft}>
            <FiPlus style={{ marginRight: '6px' }} /> {t('gp_add_row')}
          </button>
        </div>
      </main>

      {/* Gold adjust modal (reset / subtract) */}
      {showGoldModal && (
        <GoldAdjustModal
          current={goldClosing}
          onCancel={() => setShowGoldModal(false)}
          onApply={applyGoldAction}
          t={t}
        />
      )}

      {/* TEOR calculator (not saved) */}
      {showTeor && (
        <TeorCalculator onClose={() => setShowTeor(false)} t={t} />
      )}

    </div>
  );
}

// TEOR calculator popup. Nothing is saved.
// Formula: (gewicht in water / gewicht werkelijk) * A - B  (constants internal)
const TEOR_A = 2307.454;
const TEOR_B = 2088.136;

function TeorCalculator({ onClose, t }) {
  const [water, setWater] = useState('');
  const [werkelijk, setWerkelijk] = useState('');

  const w = parseFloat(water);
  const r = parseFloat(werkelijk);
  const valid = Number.isFinite(w) && Number.isFinite(r) && r !== 0;
  const teor = valid ? (w / r) * TEOR_A - TEOR_B : null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-header"><h3>{t('gp_teor_calc')}</h3></div>
        <div className="modal-body">
          <label>
            <span>{t('gp_teor_actual')} (g)</span>
            <input type="number" step="0.01" value={werkelijk} onChange={(e) => setWerkelijk(e.target.value)} placeholder="0.00" autoFocus />
          </label>
          <label>
            <span>{t('gp_teor_water')} (g)</span>
            <input type="number" step="0.01" value={water} onChange={(e) => setWater(e.target.value)} placeholder="0.00" />
          </label>

          <div className="gp-teor-result">
            <span className="muted small">TEOR</span>
            <span className="gp-teor-value">{teor !== null ? `${teor.toFixed(2)} %` : '—'}</span>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-primary" onClick={onClose}>{t('close')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- Small presentational bits ---- */

// Editable draft row (new or editing an existing row). Computes live totals and
// remaining-amount placeholders. Confirm (✓) commits, cancel (✕) discards.
function DraftRow({ note, draft, defaultRate, saving, onField, onConfirm, onCancel, t }) {
  const grams = parseFloat(draft.grams) || 0;
  const price = parseFloat(draft.price_per_gram) || 0;
  const rate = parseFloat(draft.rate_srd) || defaultRate;
  const totalUsd = grams * price;
  const totalSrd = totalUsd * rate;

  const remUsd = totalUsd
    - (parseFloat(draft.paid_usd) || 0)
    - (rate ? (parseFloat(draft.paid_srd) || 0) / rate : 0);
  const rem = remUsd > 0.005 ? remUsd : 0;
  const phUsd = rem ? money(rem) : '—';
  const phSrd = rem ? money(rem * rate) : '—';

  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); onConfirm(); }
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };

  return (
    <tr className="gp-draft-row" onKeyDown={onKey}>
      <td className="gp-note">{note}</td>
      <td className="gp-num">
        <input className="gp-cell" type="number" step="0.01" inputMode="decimal" autoFocus
          value={editVal(draft.grams)} onChange={(e) => onField('grams', e.target.value)} placeholder="0.00" />
      </td>
      <td className="gp-num">
        <input className="gp-cell" type="number" step="0.01" inputMode="decimal"
          value={editVal(draft.price_per_gram)} onChange={(e) => onField('price_per_gram', e.target.value)} placeholder="0.00" />
      </td>
      <td className="gp-num gp-computed">$ {money(totalUsd)}</td>
      <td className="gp-num gp-computed">SRD {money(totalSrd)}</td>
      <td className="gp-num">
        <input className="gp-cell gp-rate-cell" type="number" step="0.001" inputMode="decimal"
          value={editVal(draft.rate_srd)} onChange={(e) => onField('rate_srd', e.target.value)}
          placeholder={String(defaultRate)} />
      </td>
      <td className="gp-num gp-paid-col">
        <input className="gp-cell" type="number" step="0.01" inputMode="decimal"
          value={editVal(draft.paid_usd)} onChange={(e) => onField('paid_usd', e.target.value)} placeholder={phUsd} />
      </td>
      <td className="gp-num gp-paid-col">
        <input className="gp-cell" type="number" step="0.01" inputMode="decimal"
          value={editVal(draft.paid_srd)} onChange={(e) => onField('paid_srd', e.target.value)} placeholder={phSrd} />
      </td>
      <td className="gp-num gp-paid-col">
        <input className="gp-cell" type="number" step="0.01" inputMode="decimal"
          value={editVal(draft.paid_eur)} onChange={(e) => onField('paid_eur', e.target.value)} placeholder="—" />
      </td>
      <td className="gp-actions-col">
        <div className="gp-row-actions">
          <button className="btn btn-primary btn-icon" title={t('gp_confirm')} onClick={onConfirm} disabled={saving}>
            <FiCheck />
          </button>
          <button className="btn btn-ghost btn-icon" title={t('cancel')} onClick={onCancel} disabled={saving}>
            <FiX />
          </button>
        </div>
      </td>
    </tr>
  );
}

// Inline balance: three currency chips on one line.
function BalInline({ bal, tone }) {
  return (
    <div className={'gp-bal-inline' + (tone ? ` tone-${tone}` : '')}>
      {['usd', 'srd', 'eur'].map((c) => (
        <span className="gp-bal-chip" key={c}>
          <span className="gp-bal-code">{c.toUpperCase()}</span>
          <span className="gp-bal-val">{money(bal[c])}</span>
        </span>
      ))}
    </div>
  );
}

// Modal to adjust the running gold total: subtract grams (sold/used) or
// reset the total to a specific value.
function GoldAdjustModal({ current, onCancel, onApply, t }) {
  const [mode, setMode] = useState('subtract'); // 'subtract' | 'set'
  const [amount, setAmount] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt)) return;
    onApply(mode === 'set' ? 'set' : 'subtract', amt);
  };

  const preview = (() => {
    const amt = parseFloat(amount) || 0;
    return mode === 'set' ? amt : current - amt;
  })();

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-header"><h3>{t('gp_adjust_gold')}</h3></div>
        <form className="modal-body" onSubmit={submit}>
          <div className="muted small">{t('gp_gold_closing')}: <strong>{grams3(current)} g</strong></div>

          <div className="segmented" role="tablist" style={{ marginTop: '4px' }}>
            <button type="button" className={'segmented-item' + (mode === 'subtract' ? ' active' : '')} onClick={() => setMode('subtract')}>
              {t('gp_gold_subtract')}
            </button>
            <button type="button" className={'segmented-item' + (mode === 'set' ? ' active' : '')} onClick={() => setMode('set')}>
              {t('gp_gold_reset')}
            </button>
          </div>

          <label>
            <span>{mode === 'set' ? t('gp_gold_new_total') : t('gp_gold_subtract_amount')} (g)</span>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" autoFocus />
          </label>

          <div className="muted small">→ {t('gp_gold_closing')}: <strong>{grams3(preview)} g</strong></div>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onCancel}>{t('cancel')}</button>
            <button type="submit" className="btn btn-primary">{t('gp_apply')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
